import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { SealCheck, XCircle, Ticket, Trash, Plus, ChartLineUp, Users } from "@phosphor-icons/react";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "../components/ui/tabs";
import { Button } from "../components/ui/button";

export default function AdminPage() {
  const [hotels, setHotels] = useState([]);
  const [properties, setProperties] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [verifications, setVerifications] = useState([]);
  const [payoutReqs, setPayoutReqs] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [couponStats, setCouponStats] = useState(null);
  const [newCoupon, setNewCoupon] = useState({ code: "", discount_percent: 10, max_discount: 500, min_amount: 1000, kind: "any" });
  const [pendingUPI, setPendingUPI] = useState([]);
  const [upiSettings, setUpiSettings] = useState({ upi_id: "", upi_name: "" });
  const [credsStatus, setCredsStatus] = useState(null); // {email, already_changed}
  const [credsForm, setCredsForm] = useState({ old_password: "", new_email: "", new_password: "", confirm_password: "" });
  const [credsSaving, setCredsSaving] = useState(false);
  const [kycSettings, setKycSettings] = useState(null);
  const [kycForm, setKycForm] = useState({
    verhoeff_enabled: true,
    primary_provider: "none",
    cashfree: { enabled: false, cashfree_client_id: "", cashfree_client_secret: "", cashfree_base_url: "" },
    sandbox: { enabled: false, sandbox_api_key: "", sandbox_api_secret: "", sandbox_base_url: "" },
    digio: { enabled: false, digio_client_id: "", digio_client_secret: "", digio_template: "", digio_base_url: "" },
  });
  const [kycSaving, setKycSaving] = useState(false);

  const refresh = () => {
    api.get("/hotels").then(({ data }) => setHotels(data));
    api.get("/properties").then(({ data }) => setProperties(data));
    api.get("/bookings/all").then(({ data }) => setBookings(data)).catch(() => {});
    api.get("/verifications/all").then(({ data }) => setVerifications(data)).catch(() => {});
    api.get("/payouts/requests").then(({ data }) => setPayoutReqs(data)).catch(() => {});
    api.get("/admin/coupons").then(({ data }) => setCoupons(data)).catch(() => {});
    api.get("/admin/analytics/coupons").then(({ data }) => setCouponStats(data)).catch(() => {});
    api.get("/admin/payments/pending").then(({ data }) => setPendingUPI(data)).catch(() => {});
    api.get("/admin/settings/upi").then(({ data }) => setUpiSettings(data)).catch(() => {});
    api.get("/admin/credentials/status").then(({ data }) => setCredsStatus(data)).catch(() => {});
    api.get("/admin/kyc-settings").then(({ data }) => {
      setKycSettings(data);
      setKycForm({
        verhoeff_enabled: data.verhoeff_enabled ?? true,
        primary_provider: data.primary_provider || "none",
        cashfree: { enabled: !!data.cashfree?.enabled, cashfree_client_id: "", cashfree_client_secret: "", cashfree_base_url: data.cashfree?.cashfree_base_url || "" },
        sandbox: { enabled: !!data.sandbox?.enabled, sandbox_api_key: "", sandbox_api_secret: "", sandbox_base_url: data.sandbox?.sandbox_base_url || "" },
        digio: { enabled: !!data.digio?.enabled, digio_client_id: "", digio_client_secret: "", digio_template: data.digio?.digio_template || "", digio_base_url: data.digio?.digio_base_url || "" },
      });
    }).catch(() => {});
  };
  useEffect(() => { refresh(); }, []);

  const verifyUPI = async (kind, refId, approve) => {
    const note = approve ? "" : (window.prompt("Reason for rejection?") || "");
    if (!approve && !note) return;
    try {
      await api.post(`/admin/payments/${kind}/${refId}/verify`, { approve, admin_note: note });
      toast.success(approve ? "Payment verified" : "Rejected");
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const saveUpiSettings = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/settings/upi", upiSettings);
      toast.success("UPI settings updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const approve = async (id) => {
    try { await api.put(`/verifications/${id}/approve`); toast.success("Approved"); refresh(); }
    catch { toast.error("Failed"); }
  };
  const reject = async (id) => {
    try { await api.put(`/verifications/${id}/reject`); toast.info("Rejected"); refresh(); }
    catch { toast.error("Failed"); }
  };
  const markPaid = async (id) => {
    try { await api.put(`/payouts/requests/${id}/mark-paid`); toast.success("Marked paid"); refresh(); }
    catch { toast.error("Failed"); }
  };
  const toggleVerified = async (hotelId) => {
    try { const { data } = await api.put(`/admin/hotels/${hotelId}/verify`);
          toast.success(`Hotel ${data.is_verified ? "verified" : "unverified"}`); refresh(); }
    catch { toast.error("Failed"); }
  };
  const createCoupon = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/coupons", newCoupon);
      toast.success("Coupon created ✓");
      setNewCoupon({ code: "", discount_percent: 10, max_discount: 500, min_amount: 1000, kind: "any" });
      refresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };
  const deleteCoupon = async (id) => {
    if (!window.confirm("Delete this coupon?")) return;
    try { await api.delete(`/admin/coupons/${id}`); toast.success("Deleted"); refresh(); }
    catch { toast.error("Failed"); }
  };

  const submitCredsChange = async (e) => {
    e.preventDefault();
    const { old_password, new_email, new_password, confirm_password } = credsForm;
    if (!old_password) { toast.error("Current password required"); return; }
    if (!new_email && !new_password) { toast.error("Enter a new email OR new password"); return; }
    if (new_password && new_password !== confirm_password) { toast.error("New password fields do not match"); return; }
    if (new_password && new_password.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (!window.confirm("This is a ONE-TIME action. The change is permanent. Continue?")) return;
    setCredsSaving(true);
    try {
      const payload = { old_password };
      if (new_email) payload.new_email = new_email;
      if (new_password) payload.new_password = new_password;
      await api.post("/admin/credentials/change", payload);
      toast.success("Credentials updated. Please sign in again.");
      setTimeout(() => { window.location.href = "/login"; }, 1500);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setCredsSaving(false);
    }
  };

  const saveKycSettings = async (e) => {
    e.preventDefault();
    setKycSaving(true);
    try {
      // Only send non-empty new secret values; empty strings leave existing values untouched
      const payload = {
        verhoeff_enabled: kycForm.verhoeff_enabled,
        primary_provider: kycForm.primary_provider,
        cashfree: {
          enabled: kycForm.cashfree.enabled,
          cashfree_client_id: kycForm.cashfree.cashfree_client_id || null,
          cashfree_client_secret: kycForm.cashfree.cashfree_client_secret || null,
          cashfree_base_url: kycForm.cashfree.cashfree_base_url || null,
        },
        sandbox: {
          enabled: kycForm.sandbox.enabled,
          sandbox_api_key: kycForm.sandbox.sandbox_api_key || null,
          sandbox_api_secret: kycForm.sandbox.sandbox_api_secret || null,
          sandbox_base_url: kycForm.sandbox.sandbox_base_url || null,
        },
        digio: {
          enabled: kycForm.digio.enabled,
          digio_client_id: kycForm.digio.digio_client_id || null,
          digio_client_secret: kycForm.digio.digio_client_secret || null,
          digio_template: kycForm.digio.digio_template || null,
          digio_base_url: kycForm.digio.digio_base_url || null,
        },
      };
      await api.post("/admin/kyc-settings", payload);
      toast.success("Aadhaar KYC settings saved");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setKycSaving(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <p className="text-xs tracking-[0.3em] uppercase text-gold mb-3">Zyro Console</p>
      <h1 className="font-serif text-4xl lg:text-5xl text-midnight mb-10">Admin Panel</h1>

      <Tabs defaultValue="verifications">
        <TabsList className="mb-8 flex-wrap">
          <TabsTrigger value="verifications" data-testid="tab-verifications">ID Verifications ({verifications.filter(v => v.status === "pending").length})</TabsTrigger>
          <TabsTrigger value="payouts" data-testid="tab-payouts">Payouts ({payoutReqs.filter(p => p.status === "pending").length})</TabsTrigger>
          <TabsTrigger value="hotels" data-testid="tab-hotels">Hotels ({hotels.length})</TabsTrigger>
          <TabsTrigger value="properties" data-testid="tab-properties">Rentals ({properties.length})</TabsTrigger>
          <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings ({bookings.length})</TabsTrigger>
          <TabsTrigger value="coupons" data-testid="tab-coupons">Coupons ({coupons.length})</TabsTrigger>
          <TabsTrigger value="upi" data-testid="tab-upi">UPI Payments ({pendingUPI.length})</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="verifications">
          <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-stone">
                <tr className="text-left">
                  <th className="p-4 text-xs tracking-widest uppercase text-neutral-500">User</th>
                  <th className="p-4 text-xs tracking-widest uppercase text-neutral-500">Aadhaar</th>
                  <th className="p-4 text-xs tracking-widest uppercase text-neutral-500">Documents</th>
                  <th className="p-4 text-xs tracking-widest uppercase text-neutral-500">Status</th>
                  <th className="p-4 text-xs tracking-widest uppercase text-neutral-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {verifications.length === 0 ? (
                  <tr><td colSpan={5} className="p-12 text-center text-neutral-500">No verifications submitted yet</td></tr>
                ) : verifications.map((v) => (
                  <tr key={v.id} className="border-t border-black/5">
                    <td className="p-4">
                      <p className="text-midnight font-medium">{v.user_name}</p>
                      <p className="text-xs text-neutral-500">{v.user_email}</p>
                    </td>
                    <td className="p-4 font-mono text-xs">{v.aadhaar_number}</td>
                    <td className="p-4 text-xs">
                      {(v.aadhaar_front_url || v.aadhaar_url) && (
                        <a href={v.aadhaar_front_url || v.aadhaar_url} target="_blank" rel="noreferrer" className="text-gold underline block">View Aadhaar Front</a>
                      )}
                      {v.aadhaar_back_url && (
                        <a href={v.aadhaar_back_url} target="_blank" rel="noreferrer" className="text-gold underline block">View Aadhaar Back</a>
                      )}
                      {v.voter_id_url && <a href={v.voter_id_url} target="_blank" rel="noreferrer" className="text-gold underline block">View Voter ID</a>}
                    </td>
                    <td className="p-4">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        v.status === "approved" ? "bg-emerald-50 text-emerald-700" :
                        v.status === "rejected" ? "bg-red-50 text-red-700" :
                        "bg-amber-50 text-amber-700"
                      }`}>{v.status}</span>
                    </td>
                    <td className="p-4">
                      {v.status === "pending" && (
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => approve(v.id)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`approve-${v.id}`}>
                            <SealCheck size={14} className="mr-1" /> Approve
                          </Button>
                          <Button size="sm" onClick={() => reject(v.id)} variant="outline" className="text-red-700" data-testid={`reject-${v.id}`}>
                            <XCircle size={14} className="mr-1" /> Reject
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="payouts">
          <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-stone">
                <tr className="text-left">
                  <th className="p-4 text-xs tracking-widest uppercase text-neutral-500">User</th>
                  <th className="p-4 text-xs tracking-widest uppercase text-neutral-500">Amount</th>
                  <th className="p-4 text-xs tracking-widest uppercase text-neutral-500">Requested</th>
                  <th className="p-4 text-xs tracking-widest uppercase text-neutral-500">Status</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {payoutReqs.length === 0 ? (
                  <tr><td colSpan={5} className="p-12 text-center text-neutral-500">No payout requests</td></tr>
                ) : payoutReqs.map((p) => (
                  <tr key={p.id} className="border-t border-black/5">
                    <td className="p-4 font-mono text-xs">{p.user_id.slice(0, 12)}</td>
                    <td className="p-4">₹{p.amount.toLocaleString("en-IN")}</td>
                    <td className="p-4 text-xs">{new Date(p.created_at).toLocaleString("en-IN")}</td>
                    <td className="p-4">
                      <span className={`text-xs px-2 py-1 rounded-full ${p.status === "paid" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{p.status}</span>
                    </td>
                    <td className="p-4">
                      {p.status === "pending" && (
                        <Button size="sm" onClick={() => markPaid(p.id)} className="bg-midnight text-bone hover:bg-midnight-hover">Mark Paid</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="hotels">
          <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-stone">
                <tr className="text-left">
                  <th className="p-4 text-xs tracking-widest uppercase text-neutral-500">Name</th>
                  <th className="p-4 text-xs tracking-widest uppercase text-neutral-500">City</th>
                  <th className="p-4 text-xs tracking-widest uppercase text-neutral-500">Price</th>
                  <th className="p-4 text-xs tracking-widest uppercase text-neutral-500">Rating</th>
                </tr>
              </thead>
              <tbody>
                {hotels.map((h) => (
                  <tr key={h.id} className="border-t border-black/5">
                    <td className="p-4 font-medium text-midnight">{h.name}</td>
                    <td className="p-4">{h.city}</td>
                    <td className="p-4">₹{h.price_per_night.toLocaleString("en-IN")}</td>
                    <td className="p-4">{h.rating} ★</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="properties">
          <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-stone">
                <tr className="text-left">
                  <th className="p-4 text-xs tracking-widest uppercase text-neutral-500">Title</th>
                  <th className="p-4 text-xs tracking-widest uppercase text-neutral-500">Type</th>
                  <th className="p-4 text-xs tracking-widest uppercase text-neutral-500">City</th>
                  <th className="p-4 text-xs tracking-widest uppercase text-neutral-500">Rent</th>
                </tr>
              </thead>
              <tbody>
                {properties.map((p) => (
                  <tr key={p.id} className="border-t border-black/5">
                    <td className="p-4 font-medium text-midnight">{p.title}</td>
                    <td className="p-4">{p.room_type}</td>
                    <td className="p-4">{p.city}</td>
                    <td className="p-4">₹{p.monthly_rent.toLocaleString("en-IN")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="bookings">
          <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-stone">
                <tr className="text-left">
                  <th className="p-4 text-xs tracking-widest uppercase text-neutral-500">Guest</th>
                  <th className="p-4 text-xs tracking-widest uppercase text-neutral-500">Hotel</th>
                  <th className="p-4 text-xs tracking-widest uppercase text-neutral-500">Total</th>
                  <th className="p-4 text-xs tracking-widest uppercase text-neutral-500">Status</th>
                </tr>
              </thead>
              <tbody>
                {bookings.length === 0 ? (
                  <tr><td colSpan={4} className="p-8 text-center text-neutral-500">No bookings yet</td></tr>
                ) : bookings.map((b) => (
                  <tr key={b.id} className="border-t border-black/5">
                    <td className="p-4">
                      <p className="font-medium text-midnight">{b.user_name}</p>
                      <p className="text-xs text-neutral-500">{b.user_email}</p>
                    </td>
                    <td className="p-4">{b.hotel_name}</td>
                    <td className="p-4">₹{b.total_amount.toLocaleString("en-IN")}</td>
                    <td className="p-4">
                      <span className={`text-xs px-2 py-1 rounded-full ${b.status === "confirmed" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{b.status.replace("_", " ")}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="coupons">
          {couponStats && (
            <div className="mb-6 bg-gradient-to-br from-midnight to-black text-bone rounded-2xl p-6" data-testid="coupon-analytics">
              <div className="flex items-center gap-2 text-gold text-[10px] tracking-widest uppercase mb-4">
                <ChartLineUp size={14} weight="fill" /> Coupon Analytics
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div>
                  <p className="text-[10px] tracking-widest uppercase text-white/50">Total</p>
                  <p className="font-serif text-3xl text-white" data-testid="stat-total-coupons">{couponStats.total_coupons}</p>
                </div>
                <div>
                  <p className="text-[10px] tracking-widest uppercase text-white/50">Active</p>
                  <p className="font-serif text-3xl text-emerald-300">{couponStats.active}</p>
                </div>
                <div>
                  <p className="text-[10px] tracking-widest uppercase text-white/50">Redemptions</p>
                  <p className="font-serif text-3xl text-white" data-testid="stat-redemptions">{couponStats.total_redemptions}</p>
                </div>
                <div>
                  <p className="text-[10px] tracking-widest uppercase text-white/50">Discount given (est.)</p>
                  <p className="font-serif text-3xl text-gold">₹{couponStats.estimated_discount_given.toLocaleString("en-IN")}</p>
                </div>
              </div>
              <div>
                <p className="text-[10px] tracking-widest uppercase text-white/50 mb-2">Top 5 by usage</p>
                {couponStats.top5.length === 0 || couponStats.top5.every((c) => c.used_count === 0) ? (
                  <p className="text-xs text-white/50">No redemptions yet — coupons are pending first use.</p>
                ) : (
                  <div className="space-y-2">
                    {couponStats.top5.map((c) => (
                      <div key={c.code} className="flex items-center justify-between border-b border-white/10 pb-2 last:border-0">
                        <div>
                          <span className="font-mono tracking-widest text-white">{c.code}</span>
                          <span className="ml-3 text-[10px] uppercase tracking-widest text-white/40">{c.kind}</span>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-white">{c.used_count} uses</p>
                          <p className="text-[10px] text-white/40">{c.discount_percent}% off · max ₹{c.max_discount}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Create form */}
            <form onSubmit={createCoupon} className="bg-white border border-black/5 rounded-2xl p-6 space-y-4" data-testid="coupon-create-form">
              <h3 className="font-serif text-xl text-midnight flex items-center gap-2"><Plus size={16} weight="bold" className="text-gold" /> New Coupon</h3>
              <div>
                <label className="text-xs tracking-widest uppercase text-neutral-500 mb-1 block">Code</label>
                <input required value={newCoupon.code} onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value.toUpperCase() })}
                  className="w-full border border-black/10 rounded-md px-3 py-2 text-sm uppercase tracking-widest" placeholder="ZYRO20" data-testid="coupon-code-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs tracking-widest uppercase text-neutral-500 mb-1 block">Discount %</label>
                  <input type="number" min="1" max="100" required value={newCoupon.discount_percent}
                    onChange={(e) => setNewCoupon({ ...newCoupon, discount_percent: parseFloat(e.target.value) })}
                    className="w-full border border-black/10 rounded-md px-3 py-2 text-sm" data-testid="coupon-discount-input" />
                </div>
                <div>
                  <label className="text-xs tracking-widest uppercase text-neutral-500 mb-1 block">Max ₹ off</label>
                  <input type="number" min="0" value={newCoupon.max_discount}
                    onChange={(e) => setNewCoupon({ ...newCoupon, max_discount: parseFloat(e.target.value || 0) })}
                    className="w-full border border-black/10 rounded-md px-3 py-2 text-sm" data-testid="coupon-maxdisc-input" />
                </div>
                <div>
                  <label className="text-xs tracking-widest uppercase text-neutral-500 mb-1 block">Min booking ₹</label>
                  <input type="number" min="0" value={newCoupon.min_amount}
                    onChange={(e) => setNewCoupon({ ...newCoupon, min_amount: parseFloat(e.target.value || 0) })}
                    className="w-full border border-black/10 rounded-md px-3 py-2 text-sm" data-testid="coupon-minamt-input" />
                </div>
                <div>
                  <label className="text-xs tracking-widest uppercase text-neutral-500 mb-1 block">Applies to</label>
                  <select value={newCoupon.kind} onChange={(e) => setNewCoupon({ ...newCoupon, kind: e.target.value })}
                    className="w-full border border-black/10 rounded-md px-3 py-2 text-sm" data-testid="coupon-kind-select">
                    <option value="any">Both (Hotels + Rentals)</option>
                    <option value="hotel">Hotels only</option>
                    <option value="rental">Rentals only</option>
                  </select>
                </div>
              </div>
              <Button type="submit" className="bg-midnight text-bone hover:bg-midnight-hover w-full" data-testid="coupon-create-btn">
                <Ticket size={14} className="mr-2" /> Create Coupon
              </Button>
            </form>

            {/* List */}
            <div className="bg-white border border-black/5 rounded-2xl p-6" data-testid="coupon-list">
              <h3 className="font-serif text-xl text-midnight mb-4">Active Coupons</h3>
              {coupons.length === 0 ? (
                <p className="text-sm text-neutral-500 py-8 text-center">No coupons yet — create your first offer.</p>
              ) : (
                <div className="space-y-3">
                  {coupons.map((c) => (
                    <div key={c.id} className="flex items-center justify-between border border-black/5 rounded-xl p-3" data-testid={`coupon-row-${c.code}`}>
                      <div>
                        <p className="font-mono tracking-widest text-midnight">{c.code}</p>
                        <p className="text-xs text-neutral-500">
                          {c.discount_percent}% off · max ₹{c.max_discount || "—"} · min ₹{c.min_amount || 0} · <span className="uppercase">{c.kind}</span> · used {c.used_count || 0}
                        </p>
                      </div>
                      <button onClick={() => deleteCoupon(c.id)} className="p-2 rounded-full hover:bg-red-50 text-red-600" title="Delete" data-testid={`coupon-del-${c.code}`}>
                        <Trash size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="upi">
          {/* Settings card */}
          <form onSubmit={saveUpiSettings} className="bg-white border border-black/5 rounded-2xl p-6 mb-6" data-testid="upi-settings">
            <h3 className="font-serif text-xl text-midnight mb-4">Business UPI settings</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs tracking-widest uppercase text-neutral-500 mb-1 block">UPI ID</label>
                <input value={upiSettings.upi_id} onChange={(e) => setUpiSettings({ ...upiSettings, upi_id: e.target.value })}
                  className="w-full border border-black/10 rounded-md px-3 py-2 text-sm font-mono" data-testid="admin-upi-id" required />
              </div>
              <div>
                <label className="text-xs tracking-widest uppercase text-neutral-500 mb-1 block">Business name</label>
                <input value={upiSettings.upi_name} onChange={(e) => setUpiSettings({ ...upiSettings, upi_name: e.target.value })}
                  className="w-full border border-black/10 rounded-md px-3 py-2 text-sm" data-testid="admin-upi-name" required />
              </div>
            </div>
            <Button type="submit" className="mt-4 bg-midnight text-bone hover:bg-midnight-hover" data-testid="save-upi-btn">Save UPI settings</Button>
          </form>

          {/* Pending list */}
          <div className="bg-white border border-black/5 rounded-2xl p-6" data-testid="upi-pending-list">
            <h3 className="font-serif text-xl text-midnight mb-4">Pending verifications ({pendingUPI.length})</h3>
            {pendingUPI.length === 0 ? (
              <p className="text-sm text-neutral-500 py-8 text-center">No pending UPI payments right now.</p>
            ) : (
              <div className="space-y-4">
                {pendingUPI.map((p) => (
                  <div key={p.id} className={`border rounded-xl p-4 flex flex-col md:flex-row md:items-center gap-4 ${
                    p.manual_upi_instant_eligible ? "border-emerald-300 bg-emerald-50/40" : "border-black/5"
                  }`} data-testid={`upi-row-${p.id}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-full bg-stone">{p.kind}</span>
                        <span className="font-mono text-sm text-midnight">{p.manual_upi_reference}</span>
                        {p.manual_upi_instant_eligible && (
                          <span className="text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-full bg-emerald-600 text-white font-semibold">
                            ⚡ INSTANT VERIFY
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-midnight font-medium">
                        {p.kind === "booking" ? p.hotel_name : p.property_title} · {p.user_name}
                      </p>
                      <p className="text-xs text-neutral-500 flex flex-wrap items-center gap-2">
                        <span>₹{Number(p.total_amount).toLocaleString("en-IN")}</span>
                        {p.manual_upi_payer_ref ? (
                          <>
                            <span>·</span>
                            <span className="font-mono font-semibold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded">
                              UTR {p.manual_upi_payer_ref}
                            </span>
                            <button onClick={() => { navigator.clipboard.writeText(p.manual_upi_payer_ref); toast.success("UTR copied"); }}
                                    className="text-emerald-700 hover:text-midnight" title="Copy UTR">📋</button>
                          </>
                        ) : (
                          <>
                            <span>·</span>
                            <span className="text-amber-700">No UTR</span>
                          </>
                        )}
                        <span>·</span>
                        <span>{new Date(p.manual_upi_submitted_at || Date.now()).toLocaleString("en-IN")}</span>
                      </p>
                    </div>
                    {p.manual_upi_proof_file_id && (
                      <a href={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/files/${p.manual_upi_proof_file_id}?inline=1`}
                         target="_blank" rel="noreferrer"
                         className="text-xs text-gold underline">View proof</a>
                    )}
                    <div className="flex gap-2">
                      <Button onClick={() => verifyUPI(p.kind, p.id, true)} size="sm"
                              className={p.manual_upi_instant_eligible
                                ? "bg-emerald-600 hover:bg-emerald-700 text-white shadow-md"
                                : "bg-emerald-600 hover:bg-emerald-700 text-white"}
                              data-testid={`upi-approve-${p.id}`}>
                        {p.manual_upi_instant_eligible ? "⚡ Approve" : "Approve"}
                      </Button>
                      <Button onClick={() => verifyUPI(p.kind, p.id, false)} size="sm" variant="outline"
                              className="border-red-600 text-red-600 hover:bg-red-50" data-testid={`upi-reject-${p.id}`}>
                        Reject
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="settings">
          <div className="max-w-2xl bg-white border border-black/5 rounded-2xl p-8" data-testid="admin-settings-panel">
            <h3 className="font-serif text-2xl text-midnight mb-2">Change Admin Credentials</h3>
            <p className="text-sm text-neutral-600 mb-6">
              This is a <strong className="text-red-700">one-time-only</strong> action. Once changed, this option gets permanently disabled.
              Choose new email, new password, or both.
            </p>

            {credsStatus?.already_changed ? (
              <div className="p-6 bg-red-50 border border-red-200 rounded-xl text-red-900" data-testid="creds-locked">
                <p className="font-medium mb-1">🔒 One-time change already used</p>
                <p className="text-sm">
                  Current admin email: <span className="font-mono">{credsStatus.email}</span>.
                  For any further changes, contact database admin.
                </p>
              </div>
            ) : (
              <form onSubmit={submitCredsChange} className="space-y-5" data-testid="admin-creds-form">
                <div>
                  <label className="text-xs tracking-widest uppercase text-neutral-500 block mb-2">Current admin email</label>
                  <div className="p-3 bg-stone rounded-lg font-mono text-sm text-neutral-700" data-testid="current-admin-email">
                    {credsStatus?.email || "—"}
                  </div>
                </div>
                <div>
                  <label className="text-xs tracking-widest uppercase text-neutral-500 block mb-2">Current Password *</label>
                  <input type="password" required value={credsForm.old_password}
                    onChange={(e) => setCredsForm({ ...credsForm, old_password: e.target.value })}
                    data-testid="creds-old-password"
                    placeholder="Enter your current password"
                    className="w-full px-4 py-3 rounded-xl border border-black/10 focus:border-gold focus:ring-1 focus:ring-gold outline-none" />
                </div>

                <div className="pt-4 border-t border-black/5">
                  <p className="text-xs tracking-widest uppercase text-neutral-500 mb-4">New Values (leave blank to keep current)</p>
                  <div className="space-y-4">
                    <div>
                      <label className="text-xs text-neutral-500 block mb-1">New Email</label>
                      <input type="email" value={credsForm.new_email}
                        onChange={(e) => setCredsForm({ ...credsForm, new_email: e.target.value })}
                        data-testid="creds-new-email"
                        placeholder={`Keep as ${credsStatus?.email || "current"}`}
                        className="w-full px-4 py-3 rounded-xl border border-black/10 focus:border-gold outline-none" />
                    </div>
                    <div>
                      <label className="text-xs text-neutral-500 block mb-1">New Password (min 8 chars)</label>
                      <input type="password" value={credsForm.new_password}
                        onChange={(e) => setCredsForm({ ...credsForm, new_password: e.target.value })}
                        data-testid="creds-new-password"
                        placeholder="Leave blank to keep current password"
                        className="w-full px-4 py-3 rounded-xl border border-black/10 focus:border-gold outline-none" />
                    </div>
                    {credsForm.new_password && (
                      <div>
                        <label className="text-xs text-neutral-500 block mb-1">Confirm New Password</label>
                        <input type="password" value={credsForm.confirm_password}
                          onChange={(e) => setCredsForm({ ...credsForm, confirm_password: e.target.value })}
                          data-testid="creds-confirm-password"
                          placeholder="Re-enter new password"
                          className="w-full px-4 py-3 rounded-xl border border-black/10 focus:border-gold outline-none" />
                      </div>
                    )}
                  </div>
                </div>

                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
                  ⚠️ Save these credentials safely. You will be logged out after this change.
                </div>

                <Button type="submit" disabled={credsSaving}
                  className="w-full bg-midnight hover:bg-midnight-hover text-bone py-6"
                  data-testid="submit-creds-change-btn">
                  {credsSaving ? "Updating..." : "🔒 Update Credentials (One-time)"}
                </Button>
              </form>
            )}
          </div>

          {/* ==== Aadhaar KYC Providers ==== */}
          <div className="mt-8 max-w-3xl bg-white border border-black/5 rounded-2xl p-8" data-testid="admin-kyc-panel">
            <h3 className="font-serif text-2xl text-midnight mb-2">Aadhaar Verification (KYC)</h3>
            <p className="text-sm text-neutral-600 mb-6">
              Enable / disable Aadhaar verification methods. Toggle any provider ON and provide its API credentials.
              You can leave a provider ON without keys — the system just skips it. Secrets shown as masked; leave the field <em>blank</em> to keep the existing secret.
            </p>

            <form onSubmit={saveKycSettings} className="space-y-6" data-testid="kyc-settings-form">
              {/* Verhoeff */}
              <label className="flex items-start gap-3 p-4 rounded-xl border border-black/10 hover:bg-stone/50 cursor-pointer">
                <input type="checkbox" checked={kycForm.verhoeff_enabled}
                  onChange={(e) => setKycForm({ ...kycForm, verhoeff_enabled: e.target.checked })}
                  className="mt-1" data-testid="kyc-verhoeff-toggle" />
                <div className="flex-1">
                  <p className="font-medium text-midnight">✅ Verhoeff Checksum <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">Free · Instant</span></p>
                  <p className="text-xs text-neutral-500 mt-1">Blocks 90%+ fake/random Aadhaar numbers by validating UIDAI's checksum algorithm. No API key needed. Recommended: keep ON.</p>
                </div>
              </label>

              {/* Primary provider */}
              <div>
                <label className="text-xs tracking-widest uppercase text-neutral-500 block mb-2">Primary OTP Provider</label>
                <select value={kycForm.primary_provider}
                  onChange={(e) => setKycForm({ ...kycForm, primary_provider: e.target.value })}
                  data-testid="kyc-primary-select"
                  className="w-full px-4 py-3 rounded-xl border border-black/10 outline-none focus:border-gold">
                  <option value="none">None — Skip OTP verification</option>
                  <option value="cashfree">Cashfree Verification Suite</option>
                  <option value="sandbox">Sandbox API (sandbox.co.in)</option>
                  <option value="digio">Digio DigiKYC (hosted flow)</option>
                </select>
                <p className="text-xs text-neutral-500 mt-1">Users see OTP verification only if primary is set AND that provider is enabled below.</p>
              </div>

              {/* Cashfree */}
              <div className="border border-black/10 rounded-xl p-5">
                <label className="flex items-start gap-3 mb-4 cursor-pointer">
                  <input type="checkbox" checked={kycForm.cashfree.enabled}
                    onChange={(e) => setKycForm({ ...kycForm, cashfree: { ...kycForm.cashfree, enabled: e.target.checked } })}
                    className="mt-1" data-testid="kyc-cashfree-toggle" />
                  <div className="flex-1">
                    <p className="font-medium text-midnight">Cashfree Verification Suite</p>
                    <p className="text-xs text-neutral-500 mt-1">Sign up: <a href="https://merchant.cashfree.com/verificationsuite/home" target="_blank" rel="noreferrer" className="text-gold underline">merchant.cashfree.com/verificationsuite</a>. Get keys from Developers → API Keys.</p>
                    {kycSettings?.cashfree?.cashfree_client_id && (
                      <p className="text-xs text-emerald-700 mt-1">Current client_id: <span className="font-mono">{kycSettings.cashfree.cashfree_client_id}</span></p>
                    )}
                  </div>
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input placeholder="Client ID (leave blank to keep)" value={kycForm.cashfree.cashfree_client_id}
                    onChange={(e) => setKycForm({ ...kycForm, cashfree: { ...kycForm.cashfree, cashfree_client_id: e.target.value } })}
                    data-testid="kyc-cf-client-id"
                    className="px-3 py-2 rounded-lg border border-black/10 text-sm" />
                  <input type="password" placeholder="Client Secret (leave blank to keep)" value={kycForm.cashfree.cashfree_client_secret}
                    onChange={(e) => setKycForm({ ...kycForm, cashfree: { ...kycForm.cashfree, cashfree_client_secret: e.target.value } })}
                    data-testid="kyc-cf-client-secret"
                    className="px-3 py-2 rounded-lg border border-black/10 text-sm" />
                  <input placeholder="Base URL (optional, default = sandbox)" value={kycForm.cashfree.cashfree_base_url}
                    onChange={(e) => setKycForm({ ...kycForm, cashfree: { ...kycForm.cashfree, cashfree_base_url: e.target.value } })}
                    className="px-3 py-2 rounded-lg border border-black/10 text-sm md:col-span-2" />
                </div>
              </div>

              {/* Sandbox */}
              <div className="border border-black/10 rounded-xl p-5">
                <label className="flex items-start gap-3 mb-4 cursor-pointer">
                  <input type="checkbox" checked={kycForm.sandbox.enabled}
                    onChange={(e) => setKycForm({ ...kycForm, sandbox: { ...kycForm.sandbox, enabled: e.target.checked } })}
                    className="mt-1" data-testid="kyc-sandbox-toggle" />
                  <div className="flex-1">
                    <p className="font-medium text-midnight">Sandbox API (sandbox.co.in)</p>
                    <p className="text-xs text-neutral-500 mt-1">Sign up: <a href="https://accounts.sandbox.co.in/signup" target="_blank" rel="noreferrer" className="text-gold underline">accounts.sandbox.co.in/signup</a>. Console → Settings → API Keys.</p>
                    {kycSettings?.sandbox?.sandbox_api_key && (
                      <p className="text-xs text-emerald-700 mt-1">Current api_key: <span className="font-mono">{kycSettings.sandbox.sandbox_api_key}</span></p>
                    )}
                  </div>
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input placeholder="API Key (leave blank to keep)" value={kycForm.sandbox.sandbox_api_key}
                    onChange={(e) => setKycForm({ ...kycForm, sandbox: { ...kycForm.sandbox, sandbox_api_key: e.target.value } })}
                    data-testid="kyc-sb-api-key"
                    className="px-3 py-2 rounded-lg border border-black/10 text-sm" />
                  <input type="password" placeholder="API Secret (leave blank to keep)" value={kycForm.sandbox.sandbox_api_secret}
                    onChange={(e) => setKycForm({ ...kycForm, sandbox: { ...kycForm.sandbox, sandbox_api_secret: e.target.value } })}
                    data-testid="kyc-sb-api-secret"
                    className="px-3 py-2 rounded-lg border border-black/10 text-sm" />
                  <input placeholder="Base URL (optional, default = test-api)" value={kycForm.sandbox.sandbox_base_url}
                    onChange={(e) => setKycForm({ ...kycForm, sandbox: { ...kycForm.sandbox, sandbox_base_url: e.target.value } })}
                    className="px-3 py-2 rounded-lg border border-black/10 text-sm md:col-span-2" />
                </div>
              </div>

              {/* Digio */}
              <div className="border border-black/10 rounded-xl p-5">
                <label className="flex items-start gap-3 mb-4 cursor-pointer">
                  <input type="checkbox" checked={kycForm.digio.enabled}
                    onChange={(e) => setKycForm({ ...kycForm, digio: { ...kycForm.digio, enabled: e.target.checked } })}
                    className="mt-1" data-testid="kyc-digio-toggle" />
                  <div className="flex-1">
                    <p className="font-medium text-midnight">Digio DigiKYC (hosted flow)</p>
                    <p className="text-xs text-neutral-500 mt-1">Sign up: <a href="https://www.digio.in/digi-kyc/" target="_blank" rel="noreferrer" className="text-gold underline">digio.in/digi-kyc</a>. Request DigiKYC → Aadhaar Offline XML/QR template.</p>
                    {kycSettings?.digio?.digio_client_id && (
                      <p className="text-xs text-emerald-700 mt-1">Current client_id: <span className="font-mono">{kycSettings.digio.digio_client_id}</span></p>
                    )}
                  </div>
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input placeholder="Client ID (leave blank to keep)" value={kycForm.digio.digio_client_id}
                    onChange={(e) => setKycForm({ ...kycForm, digio: { ...kycForm.digio, digio_client_id: e.target.value } })}
                    className="px-3 py-2 rounded-lg border border-black/10 text-sm" />
                  <input type="password" placeholder="Client Secret (leave blank to keep)" value={kycForm.digio.digio_client_secret}
                    onChange={(e) => setKycForm({ ...kycForm, digio: { ...kycForm.digio, digio_client_secret: e.target.value } })}
                    className="px-3 py-2 rounded-lg border border-black/10 text-sm" />
                  <input placeholder="Template Name (e.g., zyro_aadhaar_offline)" value={kycForm.digio.digio_template}
                    onChange={(e) => setKycForm({ ...kycForm, digio: { ...kycForm.digio, digio_template: e.target.value } })}
                    className="px-3 py-2 rounded-lg border border-black/10 text-sm" />
                  <input placeholder="Base URL (optional)" value={kycForm.digio.digio_base_url}
                    onChange={(e) => setKycForm({ ...kycForm, digio: { ...kycForm.digio, digio_base_url: e.target.value } })}
                    className="px-3 py-2 rounded-lg border border-black/10 text-sm" />
                </div>
              </div>

              <Button type="submit" disabled={kycSaving}
                className="w-full bg-midnight hover:bg-midnight-hover text-bone py-6"
                data-testid="save-kyc-settings-btn">
                {kycSaving ? "Saving..." : "Save Aadhaar KYC Settings"}
              </Button>
            </form>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
